import * as React from 'react';
import * as styles from './styles.scss';
import {
    clearSelectedAssets,
    getDocumentLayerStyle,
    isMovingAssets,
    isPanning,
    panByWheel,
    startPanning,
    zoomByWheel,
} from '../viewstate';
import { isResizing } from 'owa-timestream-asset-sdk-resize';
import loc, { isCurrentCultureRightToLeft } from 'owa-localize';
import { jumpToRegion } from 'owa-locstrings/lib/strings/jumptoregion.locstring.json';
import { mainCanvasName } from './TimeStreamDocumentPanningLayer.locstring.json';
import { observer } from 'mobx-react-lite';
import { SkipLinkRegion } from 'owa-skip-link/lib/components/SkipLinkRegion';
import { useMouseOrPrimaryPointerEvent } from 'owa-react-hooks/lib/useMouseOrPrimaryPointerEvent';
import classNames from 'classnames';
import TimeStreamDocumentSurface from './TimeStreamDocumentSurface';
import { isMac } from 'owa-user-agent';
import throttle from 'lodash-es/throttle';

export default observer(function TimeStreamDocumentPanningLayer() {
    const canvasSkipLinkRef = React.useRef<HTMLDivElement>(null);
    const panning = isPanning();
    const movingAssets = isMovingAssets();

    const className = classNames(styles.documentLayer, {
        [styles.documentLayerShowingGridLines]: panning || movingAssets || isResizing(),
        [styles.rtl]: isCurrentCultureRightToLeft(),
    });

    const [onMouseDown, onPointerDown] = useMouseOrPrimaryPointerEvent(
        React.useCallback(
            (e: React.MouseEvent<HTMLDivElement>) => {
                if (e.button == 0 || e.button == 1) {
                    e.preventDefault();
                    e.stopPropagation();

                    canvasSkipLinkRef.current?.focus();

                    clearSelectedAssets();
                    startPanning();
                }
            },
            [canvasSkipLinkRef.current]
        )
    );

    return (
        <SkipLinkRegion
            role="main"
            ref={canvasSkipLinkRef}
            skipLinkName={loc(jumpToRegion, loc(mainCanvasName))}
            regionName={loc(mainCanvasName)}
            className={className}
            style={getDocumentLayerStyle()}
            onWheel={onWheel}
            onMouseDown={onMouseDown}
            onPointerDown={onPointerDown}>
            <TimeStreamDocumentSurface />
        </SkipLinkRegion>
    );
});

function getDocumentLayerCoordinates(event: React.WheelEvent<HTMLDivElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    return { x, y };
}

function onWheel(event: React.WheelEvent<HTMLDivElement>) {
    const openContextMenuClass = 'ms-ContextualMenu is-open';
    const canPanOrZoom = document.getElementsByClassName(openContextMenuClass).length == 0;

    if (canPanOrZoom) {
        event.stopPropagation();

        if (event.deltaY && (event.metaKey || event.ctrlKey || event.buttons == 4)) {
            event.preventDefault();
            const delta = event.deltaY < 0 ? 1 : -1;
            const layer = getDocumentLayerCoordinates(event);
            isMac() ? throttledZoomByWheel(delta, layer) : zoomByWheel(delta, layer);
        } else {
            panByWheel(event.deltaX, event.deltaY);
        }
    }
}

// 200ms window is chosen here otherwise the zooming is too slow/faster otherwise
const throttledZoomByWheel = throttle((delta, layer) => {
    zoomByWheel(delta, layer);
}, 200);
